import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createProject, getProjectById, updateProject, resetProject, clearError } from '../redux/slice/project.slice';
import { getAllCategories } from '../redux/slice/category.slice';
import { ArrowLeft, X, Upload, Image as ImageIcon, CheckCircle, MapPin, FileText, Layout, Info, Building2, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LocationPicker from '../components/LocationPicker';
import { AMENITIES_LIST } from '../constants/amenities';

const CONFIGURATION_OPTIONS = [
    '1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK', 'Penthouse', 'Villa'
];

const HelperText = ({ text }) => (
    <p className="mt-1.5 text-xs text-slate-500 flex items-start gap-1.5 pl-1">
        <Info className="w-3.5 h-3.5 text-secondary flex-shrink-0 mt-0.5" />
        <span className="italic opacity-80">{text}</span>
    </p>
);

const SectionHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-3 mb-4 sm:mb-6 border-b border-gray-100 pb-3 sm:pb-4">
        <div className="p-2.5 bg-slate-50 rounded-lg text-secondary">
            <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-serif font-semibold text-primary">{title}</h2>
    </div>
);

const MultiSelect = ({ label, options, value = [], onChange, error, touched, example }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleOption = (optionName) => {
        const newValue = Array.isArray(value) && value.includes(optionName)
            ? value.filter(item => item !== optionName)
            : [...(Array.isArray(value) ? value : []), optionName];
        onChange(newValue);
    };

    return (
        <div className="bg-slate-50/50 p-4 sm:p-5 rounded-xl border border-slate-100 hover:shadow-sm transition-all" ref={containerRef}>
            <label className="block text-sm font-semibold text-primary mb-2.5">
                {label} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
                <div
                    className="w-full px-3 sm:px-4 py-2.5 bg-white border border-gray-200 rounded-lg cursor-pointer flex justify-between items-center hover:border-secondary transition-colors"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <span className="text-gray-700 truncate font-medium">
                        {value && value.length > 0 ? `${value.length} selected` : `Select options...`}
                    </span>
                    <span className="text-gray-400 text-xs">▼</span>
                </div>

                {isOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl max-h-80 overflow-y-auto grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 p-2 sm:p-3">
                        {options.map((option) => {
                            const optName = typeof option === 'string' ? option : option.name;
                            const Icon = typeof option === 'object' ? option.icon : Layout;
                            const isSelected = Array.isArray(value) && value.includes(optName);

                            return (
                                <div
                                    key={optName}
                                    className={`relative flex flex-col justify-between p-3 rounded-xl border transition-all cursor-pointer hover:shadow-md ${isSelected ? 'border-secondary bg-secondary/5' : 'border-gray-100 hover:border-gray-200'}`}
                                    onClick={() => toggleOption(optName)}
                                >
                                    <div className="flex justify-between w-full mb-3">
                                        <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-secondary/10 text-secondary' : 'bg-slate-50 text-slate-400'}`}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors border ${isSelected ? 'border-secondary bg-secondary' : 'border-gray-200 bg-white'}`}>
                                            {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                        </div>
                                    </div>
                                    <span className={`text-xs sm:text-sm font-medium line-clamp-2 ${isSelected ? 'text-primary' : 'text-slate-600'}`}>{optName}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {touched && error && (
                <p className="mt-1 text-sm text-red-500 font-medium">{error}</p>
            )}
            {example && <HelperText text={example} />}

            {/* Selected Items Tags */}
            {value && value.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {value.map((item) => (
                        <span key={item} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary/10 text-primary border border-secondary/20 shadow-sm">
                            {(() => {
                                const opt = options.find(o => (typeof o === 'string' ? o : o.name) === item);
                                const Icon = opt && typeof opt === 'object' ? opt.icon : null;
                                return Icon ? <Icon className="w-3 h-3 mr-1.5 opacity-70" /> : null;
                            })()}
                            {item}
                            <button
                                type="button"
                                onClick={() => toggleOption(item)}
                                className="ml-2 inline-flex items-center justify-center text-secondary/60 hover:text-red-500 focus:outline-none transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const DynamicList = ({ label, items, onChange, example, placeholder, validate }) => {
    const [newValue, setNewValue] = useState('');
    const [localError, setLocalError] = useState('');

    const addItem = () => {
        const val = newValue.trim();
        if (val) {
            if (validate) {
                const err = validate(val);
                if (err) {
                    setLocalError(err);
                    return;
                }
            }
            onChange([...items, val]);
            setNewValue('');
            setLocalError('');
        }
    };

    const removeItem = (index) => {
        onChange(items.filter((_, i) => i !== index));
    };

    return (
        <div className="bg-slate-50/50 p-4 sm:p-5 rounded-xl border border-slate-100 hover:shadow-sm transition-all">
            <label className="block text-sm font-semibold text-primary mb-2.5">
                {label} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
                <input
                    type="text"
                    value={newValue}
                    onChange={(e) => {
                        setNewValue(e.target.value);
                        if (localError) setLocalError('');
                    }}
                    onBlur={addItem}
                    className={`flex-1 min-w-0 px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-all outline-none bg-white font-medium text-gray-700 placeholder:text-gray-400 placeholder:font-normal text-sm sm:text-base ${localError ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder={placeholder || `Add ${label} item`}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
                />
                <button
                    type="button"
                    onClick={addItem}
                    className="px-3 sm:px-5 py-2 bg-primary text-white font-medium rounded-lg hover:bg-black transition-colors shadow-sm active:transform active:scale-95 text-sm sm:text-base whitespace-nowrap"
                >
                    Add
                </button>
            </div>
            {localError && <p className="text-xs text-red-500 mb-2 font-medium">{localError}</p>}
            {example && <HelperText text={example} />}

            {items && items?.length > 0 && (
                <ul className="mt-3 space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                    {items?.map((item, index) => (
                        <li key={index} className="flex justify-between items-center bg-white px-4 py-2.5 rounded-lg border border-gray-100 shadow-sm group hover:border-gray-200">
                            <span className="text-sm text-gray-700 font-medium">{item}</span>
                            <button type="button" onClick={() => removeItem(index)} className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-all opacity-0 group-hover:opacity-100">
                                <X size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ProjectForm = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { project, loading, error } = useSelector((state) => state.project);
    const { categories } = useSelector((state) => state.category);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);


    useEffect(() => {
        dispatch(clearError());
        dispatch(getAllCategories());
        if (isEdit) {
            dispatch(getProjectById(id));
        } else {
            dispatch(resetProject());
        }
    }, [dispatch, id, isEdit]);

    const validationSchema = Yup.object({
        title: Yup.string().required('Title is required'),
        description: Yup.string().required('Description is required'),
        category: Yup.string().required('Category is required'),
        status: Yup.string().oneOf(['Ongoing', 'Completed']).required('Status is required'),
        bookingStatus: Yup.string().required('Booking Status is required'),
        officeAddress: Yup.string().required('Office Address is required'),
        officePhoneNumbers: Yup.array().of(Yup.string().matches(/^[0-9]+$/, "Must be only digits").min(10, 'Must be exactly 10 digits').max(10, 'Must be exactly 10 digits')).min(1, 'At least one phone number is required'),
        startDate: Yup.date().required('Start Date is required'),
        projectArea: Yup.number().required('Project Area is required').positive('Must be a positive number'),
        configuration: Yup.array().of(Yup.string()).when('category', {
            is: (val) => {
                const category = categories?.find(c => c._id === val);
                return category?.name?.toLowerCase().includes('resident');
            },
            then: (schema) => schema.min(1, 'Configuration is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        // mapUrl: Yup.string().required('Map URL is required').matches(/<iframe.*<\/iframe>/, 'Invalid iframe format'),
        location: Yup.object().shape({
            lat: Yup.number().required(),
            lng: Yup.number().required()
        }).required('Location is required'),
        structure: Yup.array().of(Yup.string()).min(1, 'At least one item is required'),
        flooring: Yup.array().of(Yup.string()).min(1, 'At least one item is required'),
        kitchen: Yup.array().of(Yup.string()).min(1, 'At least one item is required'),
        bathroom: Yup.array().of(Yup.string()).min(1, 'At least one item is required'),
        amenities: Yup.array().of(Yup.string()).min(1, 'Please select at least one amenity'),
        bluePrint: Yup.array().min(1, 'At least one blueprint is required'),
        brochure: Yup.mixed().required('Brochure is required'),
        images: Yup.array()
            .min(1, 'At least one image is required')
            .test('is-valid', 'Invalid image data', (value) =>
                Array.isArray(value) && value.every(img =>
                    img && (img.isExisting || img.file)
                )
            )
    });

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
            category: '',
            status: 'Ongoing',
            bookingStatus: 'Open',
            officeAddress: '',

            officePhoneNumbers: [],
            startDate: '',
            projectArea: '',
            configuration: [],
            // mapUrl: '',
            location: null,
            structure: [],
            flooring: [],
            kitchen: [],
            bathroom: [],
            amenities: [],
            brochure: null,
            bluePrint: [],
            images: [],
        },
        validationSchema,
        onSubmit: async (values) => {
            console.log(values);
            const formData = new FormData();
            formData.append('title', values.title);
            if (id) {
                formData.append('project_id', id);
            }
            formData.append('description', values.description);
            formData.append('category', values.category);
            formData.append('status', values.status);
            formData.append('bookingStatus', values.bookingStatus);
            formData.append('officeAddress', values.officeAddress);
            formData.append('officePhoneNumbers', JSON.stringify(values.officePhoneNumbers));
            formData.append('startDate', values.startDate);
            formData.append('projectArea', values.projectArea);
            formData.append('configuration', JSON.stringify(values.configuration));
            // formData.append('mapUrl', values.mapUrl);
            if (values.location) {
                formData.append('location', JSON.stringify(values.location));
            }
            formData.append('structure', JSON.stringify(values.structure));
            formData.append('flooring', JSON.stringify(values.flooring));
            formData.append('kitchen', JSON.stringify(values.kitchen));
            formData.append('bathroom', JSON.stringify(values.bathroom));
            formData.append('amenities', JSON.stringify(values.amenities));

            if (values.brochure instanceof File) {
                formData.append('brochure', values.brochure);
            }

            // Handle images with preserved order
            values.images.forEach((img, index) => {
                if (img.isExisting) {
                    const existingData = JSON.stringify({
                        url: img.url,
                        public_id: img.id.toString().startsWith('existing-') ? 'legacy' : img.id
                    });
                    formData.append(`images[${index}]`, existingData);
                } else if (img.file) {
                    formData.append(`images[${index}]`, img.file);
                }
            });

            // const existingImages = [];
            // values.images.forEach((img) => {
            //     if (img.isExisting) {
            //         existingImages.push({
            //             url: img.url,
            //             public_id: img.id.toString().startsWith('existing-') ? 'legacy' : img.id
            //         });
            //     } else if (img.file) {
            //         formData.append('images', img.file);
            //     }
            // });

            // if (existingImages.length > 0) {
            //     formData.append('existingImages', JSON.stringify(existingImages));
            // }

            // Handle blueprints with preserved order
            if (values.bluePrint && values.bluePrint.length > 0) {
                values.bluePrint.forEach((bp, index) => {
                    if (bp.isExisting) {
                        const existingData = JSON.stringify({
                            url: bp.url,
                            public_id: bp.public_id || 'legacy/bp'
                        });
                        formData.append(`bluePrint[${index}]`, existingData);
                    } else if (bp.file) {
                        formData.append(`bluePrint[${index}]`, bp.file);
                    }
                });
            }

            // const existingBluePrints = [];
            // if (values.bluePrint && values.bluePrint.length > 0) {
            //     values.bluePrint.forEach((bp) => {
            //         if (bp.isExisting) {
            //             existingBluePrints.push({
            //                 url: bp.url,
            //                 public_id: bp.public_id || 'legacy/bp'
            //             });
            //         } else if (bp.file) {
            //             formData.append('bluePrint', bp.file);
            //         }
            //     });
            // }
            // if (existingBluePrints.length > 0) {
            //     formData.append('existingBluePrints', JSON.stringify(existingBluePrints));
            // }

            let resultAction;
            if (isEdit) {
                resultAction = await dispatch(updateProject({ id, data: formData }));
            } else {
                resultAction = await dispatch(createProject(formData));
            }

            if (createProject.fulfilled.match(resultAction) || updateProject.fulfilled.match(resultAction)) {
                navigate('/projects');
            }
        },
    });

    useEffect(() => {
        // console.log(project, isEdit, "qqqqq");
        if (isEdit && project) {
            const normalizedImages = project.images && project.images.length > 0
                ? project.images.map(img => ({
                    id: img._id || `existing-${Math.random().toString(36).substr(2, 9)}`,
                    url: img.url,
                    isExisting: true
                }))
                : [];

            formik.setValues({
                title: project.title || '',
                description: project.description || '',
                category: project.category || '',
                status: project.status || 'Ongoing',
                bookingStatus: project.bookingStatus || 'Open',
                officeAddress: project.officeAddress || '',

                officePhoneNumbers: project.officePhoneNumbers || [],
                startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
                projectArea: project.projectArea || '',
                configuration: project.configuration || [],
                // mapUrl: project.mapUrl || '',
                location: project.location || null,
                structure: project.structure || [],
                flooring: project.flooring || [],
                kitchen: project.kitchen || [],
                bathroom: project.bathroom || [],
                amenities: project.amenities || [],
                brochure: project.brochure || null,
                bluePrint: project.bluePrint && project.bluePrint.length > 0
                    ? project.bluePrint.map(bp => ({
                        ...bp,
                        isExisting: true,
                        name: 'Blue Print',
                        id: bp._id || `existing-bp-${Math.random().toString(36).substr(2, 9)}`
                    }))
                    : [],
                images: normalizedImages,
            });
        }
    }, [project, isEdit]);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    }, []);

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const handleFiles = async (files) => {
        const validFiles = Array.from(files).filter(file => {
            const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                alert(`${file.name} is not a valid image format`);
                return false;
            }
            if (file.size > 100 * 1024 * 1024) {
                alert(`${file.name} exceeds the 100MB limit`);
                return false;
            }
            return true;
        });

        const imagePromises = validFiles.map(async (file) => {
            try {
                const base64 = await fileToBase64(file);
                return {
                    id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    file,
                    preview: base64,
                    isExisting: false,
                    name: file.name
                };
            } catch (error) {
                console.error('Error reading file:', file.name, error);
                return null;
            }
        });

        const newImages = (await Promise.all(imagePromises)).filter(img => img !== null);
        formik.setFieldValue('images', [...formik.values.images, ...newImages]);
    };

    const handleChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
        e.target.value = '';
    };

    const removeImage = (index) => {
        const newImages = formik.values.images.filter((_, i) => i !== index);
        formik.setFieldValue('images', newImages);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination } = result;

        if (source.droppableId === 'images') {
            const items = Array.from(formik.values.images);
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);
            formik.setFieldValue('images', items);
        } else if (source.droppableId === 'blueprints') {
            const items = Array.from(formik.values.bluePrint);
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);
            formik.setFieldValue('bluePrint', items);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="min-h-screen bg-surface font-sans text-primary">
            {/* Top Navigation Bar with Blur Effect */}
            <div className="sticky top-0 z-[11] bg-white/80 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-start lg:items-center h-auto lg:h-20 py-4 lg:py-0 gap-4 lg:gap-0">
                        <div className="flex items-center gap-4">
                            <Link
                                to="/projects"
                                className="p-2 ml-[-8px] text-slate-400 hover:text-secondary hover:bg-secondary/5 rounded-full transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="text-lg md:text-2xl font-serif font-bold text-primary tracking-tight">
                                    {isEdit ? 'Edit Property' : 'Add Property'}
                                </h1>
                                <p className="hidden sm:block text-xs text-slate-500 font-medium tracking-wide uppercase">
                                    {isEdit ? 'Update Details' : 'New Listing'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link
                                to="/projects"
                                className="px-3 py-2 md:px-5 md:py-2.5 border border-gray-200 text-gray-600 font-medium text-xs md:text-sm rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </Link>
                            <button
                                type="button"
                                onClick={formik.handleSubmit}
                                disabled={loading}
                                className="px-4 py-2 md:px-6 md:py-2.5 bg-primary text-white font-medium text-xs md:text-sm rounded-xl hover:bg-tertiary transition-all shadow-lg shadow-blue-900/10 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span className="hidden sm:inline">Saving...</span>
                                        <span className="sm:hidden">Saving</span>
                                    </>
                                ) : (

                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="hidden sm:inline">
                                            {isEdit ? 'Save Changes' : 'Create Property'}
                                        </span>
                                        <span className="sm:hidden">
                                            {isEdit ? 'Save' : 'Create'}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto  md:px-6 lg:px-8 py-2 md:py-8 lg:py-10">

                {error && (
                    <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 flex items-center gap-3 shadow-sm animate-fade-in">
                        <div className="p-2 bg-white rounded-full shadow-sm text-red-500">
                            <X className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm sm:text-base">{error}</span>
                    </div>
                )}

                <DragDropContext onDragEnd={onDragEnd}>
                    <form onSubmit={formik.handleSubmit} className="space-y-6 sm:space-y-8">
                        {console.log(formik.errors)}
                        {/* SECTION 1: Project Overview */}
                        <div className="bg-white p-3 sm:p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100">
                            <SectionHeader title="Property Overview" icon={Building2} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 md:gap-x-8 md:gap-y-6">
                                {/* Title */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Property Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className="w-full px-3 sm:px-5 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-slate-50/50 text-base sm:text-lg font-medium placeholder:font-sans placeholder:font-normal"
                                        placeholder="e.g. Valletta Serenity"
                                    />
                                    {formik.touched.title && formik.errors.title ? (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.title}</p>
                                    ) : (
                                        <HelperText text="A unique and attractive name for the property" />
                                    )}
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            name="category"
                                            value={formik.values.category}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none appearance-none bg-white text-gray-700 font-medium text-sm sm:text-base"
                                        >
                                            <option value="">Select Category</option>
                                            {categories?.map((cat) => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                            <span className="text-gray-400">▼</span>
                                        </div>
                                    </div>
                                    {formik.touched.category && formik.errors.category ? (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.category}</p>
                                    ) : (
                                        <HelperText text="Classify the property type" />
                                    )}
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Construction Status <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-fit p-1 bg-slate-100 rounded-xl">
                                        {['Ongoing', 'Completed'].map((status) => (
                                            <label key={status} className={`relative flex items-center justify-center px-4 py-2.5 rounded-lg cursor-pointer transition-all font-medium text-sm select-none ${formik.values.status === status ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}>
                                                <input
                                                    type="radio"
                                                    name="status"
                                                    value={status}
                                                    checked={formik.values.status === status}
                                                    onChange={formik.handleChange}
                                                    className="sr-only"
                                                />
                                                {status}
                                            </label>
                                        ))}
                                    </div>
                                    <HelperText text="Current stage of the project" />
                                </div>

                                {/* Start Date */}
                                <div>
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Project Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={formik.values.startDate}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-white text-gray-700 font-medium text-sm sm:text-base"
                                    />
                                    {formik.touched.startDate && formik.errors.startDate ? (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.startDate}</p>
                                    ) : (
                                        <HelperText text="Date when the project construction started" />
                                    )}
                                </div>

                                {/* Project Area */}
                                <div>
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Project Area (sq. ft) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="projectArea"
                                        value={formik.values.projectArea}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        min="0"
                                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-white text-gray-700 font-medium text-sm sm:text-base"
                                        placeholder="e.g. 50000"
                                    />
                                    {formik.touched.projectArea && formik.errors.projectArea ? (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.projectArea}</p>
                                    ) : (
                                        <HelperText text="Total area of the project in square feet" />
                                    )}
                                </div>

                                {/* Booking Status */}
                                <div>
                                    <label className="block text-sm font-semibold text-primary mb-2">Booking Status <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <select
                                            name="bookingStatus"
                                            value={formik.values.bookingStatus}
                                            onChange={formik.handleChange}
                                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none appearance-none bg-white text-gray-700 font-medium text-sm sm:text-base"
                                        >
                                            <option value="Open">Open</option>
                                            <option value="Closed">Closed</option>
                                            <option value="Sold Out">Sold Out</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                            <span className="text-gray-400">▼</span>
                                        </div>
                                    </div>
                                    {formik.touched.bookingStatus && formik.errors.bookingStatus && (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.bookingStatus}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-primary mb-2">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        rows={5}
                                        className="w-full px-3 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-slate-50/50 leading-relaxed placeholder:text-gray-400 text-sm sm:text-base"
                                        placeholder="Write a compelling description highlighting unique selling points, lifestyle features, and key benefits..."
                                    />
                                    {formik.touched.description && formik.errors.description && (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* SECTION 2: Location & Contact */}
                        <div className="bg-white p-3 sm:p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100">
                            <SectionHeader title="Location & Contact" icon={MapPin} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 md:gap-x-8 md:gap-y-6">

                                {/* Office Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-primary mb-2">Office / Site Address <span className="text-red-500">*</span></label>
                                    <div className="relative group">
                                        <MapPin className="absolute top-3.5 left-4 w-5 h-5 text-slate-400 group-focus-within:text-secondary transition-colors" />
                                        <textarea
                                            name="officeAddress"
                                            value={formik.values.officeAddress}
                                            onChange={formik.handleChange}
                                            rows={2}
                                            className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-white text-sm sm:text-base"
                                            placeholder="Enter complete physical address"
                                        />
                                    </div>
                                    {formik.touched.officeAddress && formik.errors.officeAddress && (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.officeAddress}</p>
                                    )}
                                </div>

                                {/* Location Picker */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-primary mb-2">Property Location <span className="text-red-500">*</span></label>
                                    <div className="w-full h-[300px] sm:h-[400px] rounded-xl overflow-hidden border border-gray-200 relative z-0">
                                        <LocationPicker
                                            value={formik.values.location}
                                            onChange={(val) => formik.setFieldValue('location', val)}
                                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                            error={formik.errors.location}
                                            touched={formik.touched.location}
                                        />
                                    </div>
                                    {formik.touched.location && formik.errors.location && (
                                        <p className="mt-1 text-sm text-red-500 font-medium">{
                                            typeof formik.errors.location === 'string' ? formik.errors.location : 'Location is required'
                                        }</p>
                                    )}
                                    <HelperText text="Search or click on the map to pin the exact location" />
                                </div>

                                {/* Office Phone Numbers */}
                                <div className="md:col-span-2">
                                    <DynamicList
                                        label="Contact Numbers"
                                        items={formik.values.officePhoneNumbers}
                                        onChange={(items) => formik.setFieldValue('officePhoneNumbers', items)}
                                        placeholder="Enter 10-digit number"
                                        example="Add numbers for direct inquiries"
                                        validate={(val) => {
                                            if (!/^\d{10}$/.test(val)) return "Phone number must be exactly 10 digits";
                                            return null;
                                        }}
                                    />
                                    {formik.touched.officePhoneNumbers && formik.errors.officePhoneNumbers && (
                                        <p className="mt-1 text-sm text-red-500 font-medium px-1 pt-1">{
                                            typeof formik.errors.officePhoneNumbers === 'string'
                                                ? formik.errors.officePhoneNumbers
                                                : 'At least one phone number is required'
                                        }</p>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* SECTION 3: Specifications */}
                        <div className="bg-white p-3 sm:p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100">
                            <SectionHeader title="Specifications & Details" icon={Layout} />

                            {/* Configuration */}
                            {categories?.find(c => c._id === formik.values.category)?.name?.toLowerCase().includes('resident') && (
                                <div className="mb-6 sm:mb-8 p-1">
                                    <MultiSelect
                                        label="Unit Configurations"
                                        options={CONFIGURATION_OPTIONS}
                                        value={formik.values.configuration}
                                        onChange={(items) => formik.setFieldValue('configuration', items)}
                                        error={formik.errors.configuration}
                                        touched={formik.touched.configuration}
                                        example="Select all available unit types"
                                    />
                                </div>
                            )}
                            {formik.touched.configuration && formik.errors.configuration && <p className="text-red-500 text-sm mt-1">{formik.errors.configuration}</p>}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 md:gap-x-8 md:gap-y-6">
                                <div>
                                    <DynamicList
                                        label="Structure"
                                        items={formik.values.structure}
                                        onChange={(items) => formik.setFieldValue('structure', items)}
                                        placeholder="e.g. RCC Frame Structure"
                                        example="Details about the building framework"
                                    />
                                    {formik.touched.structure && formik.errors.structure && <p className="text-red-500 text-sm mt-1">{formik.errors.structure}</p>}
                                </div>

                                <div>
                                    <DynamicList
                                        label="Flooring"
                                        items={formik.values.flooring}
                                        onChange={(items) => formik.setFieldValue('flooring', items)}
                                        placeholder="e.g. Vitrified Tiles"
                                        example="Materials used for flooring"
                                    />
                                    {formik.touched.flooring && formik.errors.flooring && <p className="text-red-500 text-sm mt-1">{formik.errors.flooring}</p>}
                                </div>

                                <div>
                                    <DynamicList
                                        label="Kitchen & Storage"
                                        items={formik.values.kitchen}
                                        onChange={(items) => formik.setFieldValue('kitchen', items)}
                                        placeholder="e.g. Granite Platform"
                                        example="Kitchen specs and storage facilities"
                                    />
                                    {formik.touched.kitchen && formik.errors.kitchen && <p className="text-red-500 text-sm mt-1">{formik.errors.kitchen}</p>}
                                </div>

                                <div>
                                    <DynamicList
                                        label="Plumbing & Bathrooms"
                                        items={formik.values.bathroom}
                                        onChange={(items) => formik.setFieldValue('bathroom', items)}
                                        placeholder="e.g. Branded Sanitary Ware"
                                        example="Bathroom fittings and plumbing details"
                                    />
                                    {formik.touched.bathroom && formik.errors.bathroom && <p className="text-red-500 text-sm mt-1">{formik.errors.bathroom}</p>}
                                </div>
                            </div>
                        </div>

                        {/* SECTION 4: Amenities */}
                        <div className="bg-white p-3 sm:p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100">
                            <SectionHeader title="Amenities & Facilities" icon={Zap} />
                            <MultiSelect
                                label="Project Amenities"
                                options={AMENITIES_LIST}
                                value={formik.values.amenities}
                                onChange={(items) => formik.setFieldValue('amenities', items)}
                                error={formik.errors.amenities}
                                touched={formik.touched.amenities}
                                example="Select amenities available effectively for residents"
                            />
                        </div>

                        {/* SECTION 5: Media & Downloads */}
                        <div className="bg-white p-3 sm:p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100">
                            <SectionHeader title="Media & Downloads" icon={ImageIcon} />

                            {/* Brochure Upload */}
                            <div className="mb-6 sm:mb-8">
                                <label className="block text-sm font-semibold text-primary mb-3">Project Brochure (PDF) <span className="text-red-500">*</span></label>

                                <div className={`border-2 border-dashed cursor-pointer group rounded-xl p-4 sm:p-6 md:p-8 text-center transition-all ${formik.values.brochure ? 'border-secondary/50 bg-secondary/5' : 'border-gray-300 hover:border-secondary hover:bg-slate-50'}`}>
                                    {!formik.values.brochure ? (
                                        <>
                                            <label className="flex flex-col items-center cursor-pointer">
                                                <div className="p-3 bg-slate-100 rounded-full mb-3 text-slate-500">
                                                    <FileText className="w-6 h-6" />
                                                </div>
                                                <label className="cursor-pointer" htmlFor="brochure-upload">
                                                    <span className="text-secondary font-medium group-hover:underline cursor-pointer">Click to upload</span>
                                                    {/* <span className="text-gray-500"> or drag and drop</span> */}
                                                    <input
                                                        id="brochure-upload"
                                                        type="file"
                                                        accept="application/pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files[0]) {
                                                                formik.setFieldValue('brochure', e.target.files[0]);
                                                            }
                                                        }}
                                                        className="hidden"
                                                    />
                                                </label>
                                                <p className="text-xs text-slate-400 mt-2">PDF up to 10MB</p>
                                            </label>
                                        </>
                                    ) : (
                                        <div className="flex items-center justify-between bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm w-full">
                                            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                                <div className="p-2.5 sm:p-3 bg-red-50 text-red-500 rounded-lg flex-shrink-0">
                                                    <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                                                </div>
                                                <div className="text-left min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-primary truncate" title={formik.values.brochure instanceof File ? formik.values.brochure.name : 'Current Brochure'}>
                                                        {formik.values.brochure instanceof File ? formik.values.brochure.name : 'Current Brochure'}
                                                    </p>
                                                    {!(formik.values.brochure instanceof File) && (
                                                        <a href={formik.values.brochure.url} target="_blank" rel="noreferrer" className="text-xs text-secondary hover:underline font-medium block truncate">
                                                            View Existing PDF
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => formik.setFieldValue('brochure', null)}
                                                className="ml-3 p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500 hover:text-red-500 flex-shrink-0"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {formik.touched.brochure && formik.errors.brochure && (
                                    <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.brochure}</p>
                                )}
                                <HelperText text="Upload the official brochure for users to download" />
                            </div>

                            {/* Blue Prints Upload */}
                            <div className="mb-8">
                                <label className="block text-sm font-semibold text-primary mb-3">Blueprints & Layouts <span className="text-red-500">*</span></label>

                                <input
                                    id="blueprint-upload"
                                    type="file"
                                    multiple
                                    accept="image/*,application/pdf"
                                    onChange={(e) => {
                                        if (e.target.files) {
                                            const newFiles = Array.from(e.target.files).map(file => ({
                                                file,
                                                preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
                                                name: file.name,
                                                isExisting: false,
                                                id: `new-bp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                                            }));
                                            formik.setFieldValue('bluePrint', [...formik.values.bluePrint, ...newFiles]);
                                        }
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                />

                                <Droppable droppableId="blueprints" direction="horizontal">
                                    {(provided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 mb-4"
                                        >
                                            {/* Upload Button Card */}
                                            <label
                                                htmlFor="blueprint-upload"
                                                className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-gray-300 rounded-xl hover:border-secondary hover:bg-secondary/5 cursor-pointer transition-colors group"
                                            >
                                                <div className="p-3 bg-white rounded-full shadow-sm mb-2 group-hover:scale-110 transition-transform text-secondary">
                                                    <Upload className="w-6 h-6" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-600 group-hover:text-secondary">Add Blueprint</span>
                                            </label>

                                            {formik.values.bluePrint.map((bp, index) => {
                                                const key = bp.id || `fallback-bp-${index}`;
                                                return (
                                                    <Draggable key={key} draggableId={key} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className={`relative group border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${snapshot.isDragging ? 'z-50 shadow-xl scale-105' : ''}`}
                                                                style={provided.draggableProps.style}
                                                            >
                                                                {bp.isExisting ? (
                                                                    /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(bp.url) ? (
                                                                        <img src={bp.url} alt="Blueprint" className="w-full h-full aspect-square object-cover pointer-events-none" />
                                                                    ) : (
                                                                        <div className="w-full h-full bg-slate-50 flex flex-col items-center justify-center p-2">
                                                                            <a href={bp.url} target="_blank" rel="noreferrer" className="text-secondary hover:underline text-sm font-medium flex items-center gap-1 z-10 relative">
                                                                                <FileText className="w-4 h-4" /> View File
                                                                            </a>
                                                                        </div>
                                                                    )
                                                                ) : (
                                                                    <>
                                                                        {bp.file.type.includes('image') ? (
                                                                            <img src={bp.preview} alt={bp.name} className="w-full h-full aspect-square object-cover pointer-events-none" />
                                                                        ) : (
                                                                            <div className="w-full h-full bg-slate-50 flex flex-col items-center justify-center p-2 text-center text-slate-500">
                                                                                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300 mb-2 pointer-events-none" />
                                                                                <span className="text-[10px] sm:text-xs line-clamp-2 pointer-events-none">{bp.name}</span>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const newBps = formik.values.bluePrint.filter((_, i) => i !== index);
                                                                        formik.setFieldValue('bluePrint', newBps);
                                                                    }}
                                                                    className="absolute top-2 right-2 p-1.5 bg-white text-red-500 rounded-full opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-50 z-20"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                                {formik.touched.bluePrint && formik.errors.bluePrint && (
                                    <p className="mt-1 text-sm text-red-500 font-medium">{
                                        typeof formik.errors.bluePrint === 'string'
                                            ? formik.errors.bluePrint
                                            : 'At least one blueprint is required'
                                    }</p>
                                )}
                                <HelperText text="Floor plans, master plans, and layout diagrams" />
                            </div>

                            {/* Project Images */}
                            <div>
                                <label className="block text-sm font-semibold text-primary mb-3">Gallery Images <span className="text-red-500">*</span></label>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="image/svg+xml,image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                    onChange={handleChange}
                                    className="hidden"
                                />

                                <div
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={triggerFileInput}
                                    className={`border-2 border-dashed rounded-xl p-4 sm:p-8 md:p-10 text-center cursor-pointer transition-all ${dragActive
                                        ? 'border-secondary bg-secondary/5 scale-[1.01]'
                                        : 'border-gray-200 hover:border-secondary hover:bg-slate-50'
                                        }`}
                                >
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="w-16 h-16 bg-white shadow-sm rounded-full flex items-center justify-center mb-4 text-secondary">
                                            <ImageIcon className="w-8 h-8" />
                                        </div>
                                        <p className="text-lg font-medium text-primary mb-1">
                                            Click to upload or drag and drop
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            High resolution images (max 100MB per file)
                                        </p>
                                    </div>
                                </div>
                                {formik.touched.images && formik.errors.images && (
                                    <p className="mt-2 text-sm text-red-500 font-medium">
                                        {typeof formik.errors.images === 'string'
                                            ? formik.errors.images
                                            : 'Please upload at least one table image'}
                                    </p>
                                )}

                                {/* Image Preview Grid */}
                                {formik.values.images.length > 0 && (
                                    <div className="mt-8">
                                        <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center justify-between">
                                            <span>Uploaded Gallery ({formik.values.images.length})</span>
                                            <span className="text-xs text-gray-400 font-normal">Drag to reorder</span>
                                        </h3>

                                        <Droppable droppableId="images" direction="horizontal">
                                            {(provided) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4"
                                                >
                                                    {formik.values.images.map((img, index) => {
                                                        const src = img.isExisting ? img.url : img.preview;
                                                        const key = img.id || `fallback-${index}`;

                                                        return (
                                                            <Draggable key={key} draggableId={key} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`relative group aspect-square rounded-xl overflow-hidden bg-white shadow-sm transition-all border border-gray-100 hover:shadow-md ${snapshot.isDragging ? 'z-50 shadow-xl scale-105' : ''}`}
                                                                        style={provided.draggableProps.style}
                                                                    >
                                                                        <img
                                                                            src={src}
                                                                            alt={img.name}
                                                                            className="w-full h-full aspect-square object-cover pointer-events-none"
                                                                        />
                                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />

                                                                        {/* Badge */}
                                                                        <div className="absolute top-2 left-2 pointer-events-none z-10">
                                                                            <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm shadow-sm ${img.isExisting
                                                                                ? 'bg-white/90 text-green-700'
                                                                                : 'bg-secondary text-white'
                                                                                }`}>
                                                                                {img.isExisting ? 'Live' : 'New'}
                                                                            </span>
                                                                        </div>

                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                removeImage(index);
                                                                            }}
                                                                            className="absolute top-2 right-2 p-1.5 bg-white text-red-500 rounded-full opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 shadow-sm z-20"
                                                                        >
                                                                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </DragDropContext>
            </div >
        </div >
    );
};

export default ProjectForm;
